import { Modal, Spinner } from "react-bootstrap";
import {
  projectLogoLight,
  projectPrimaryColor,
} from "../../../../generalConfigs";
import { isMobile } from "react-device-detect";
import { Link, useHistory } from "react-router-dom";
import { useState, useEffect } from "react";
import { useCep } from "../../../../hooks/useCep";
import { useCustomer } from "../../../../hooks/useCustomer";
import { useUser } from "../../../../hooks/useUser";
import useBillingContext, {
  eBillingStep,
} from "../../../../context/BillingContext";

export default function CreateCustomer() {
  const [cepValue, setCepValue] = useState<string>("");
  const [addressValue, setAddressValue] = useState<string>("");
  const [cityValue, setCityValue] = useState<string>("");
  const [stateValue, setStateValue] = useState<string>("");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { findOne } = useCep();
  const { storeOne } = useCustomer();
  const { isAuthenticated } = useUser();
  const { setStep } = useBillingContext();
  const history = useHistory();

  async function handleSubmit(e: React.FormEvent<HTMLElement>) {
    e.preventDefault();
    setIsLoading(true);
    setErrorMsg("");

    const isCustomerCreated = await storeOne({
      address: addressValue,
      city: cityValue,
      state: stateValue,
      zip: cepValue,
    });

    if (isCustomerCreated) {
      setStep(eBillingStep.PAYMENT);
    } else {
      setErrorMsg(
        "Ocorreu um erro, verifique os dados inseridos e tente novamente."
      );
    }

    setIsLoading(false);
  }

  async function handleCepFill() {
    const foundCepDetails = await findOne(cepValue);

    if (foundCepDetails) {
      setAddressValue(foundCepDetails.logradouro);
      setCityValue(foundCepDetails.localidade);
      setStateValue(foundCepDetails.uf);
    }
  }

  /**
   * Subscribe to CEP input and call VIACEP on CEP fullfiled
   */
  useEffect(() => {
    const totalCepLength = cepValue.length;

    if (totalCepLength > 7 && totalCepLength < 10) {
      handleCepFill();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cepValue]);

  /**
   * Check if user is authenticated and send user to dashboard
   * Check if user is already a customer and redirect to next page
   */
  useEffect(() => {
    async function isUserAlreadyACustomer() {
      setIsLoading(true);
      const isUserAuthenticated = isAuthenticated();
      if (!isUserAuthenticated) {
        return window.location.replace("/login");
      }

      setIsLoading(false);
    }
    isUserAlreadyACustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal size="lg" fullscreen={true} show={true}>
      <div className="bg-dark">
        <div className="container">
          <div className="row">
            <div className="col-lg-4 shadow-lg">
              <div className="hero-wrap d-flex align-items-center rounded-3 rounded-end-0 h-100">
                <div className="hero-mask opacity-9 bg-primary" />
                <div className="hero-bg hero-bg-scroll" />
                <div className="hero-content mx-auto w-100 h-100 d-flex flex-column">
                  <div className="row g-0">
                    <div className="col-11 col-lg-10 mx-auto">
                      <div className="mt-3">{projectLogoLight}</div>
                    </div>
                  </div>
                  <div className={isMobile ? "row g-0 my-3" : "row g-0 my-4"}>
                    <div className="col-11 col-lg-10 mx-auto">
                      <h1 className="text-11" style={{ color: "#e0e0e0" }}>
                        Falta <span className="markText-animation">pouco.</span>
                      </h1>
                      <p
                        className="text-5 lh-base mb-4"
                        style={{ color: "#e0e0e0" }}
                      >
                        Seus dados são protegidos com criptografia e segurança
                        de ponta a ponta.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="col-lg-8 shadow-lg d-flex align-items-center rounded-3 rounded-start-0"
              style={{
                backgroundColor: "white",
                borderRadius: 10,
              }}
            >
              <div className="container my-auto py-4">
                <div className="row">
                  <div className="col-11 col-lg-10 mx-auto">
                    <h3 className="text-black text-center mb-3 ">
                      Endereço de faturamento
                    </h3>

                    <form className="form" onSubmit={handleSubmit}>
                      <div className="mb-3">
                        <label className="form-label text-black">CEP</label>
                        <input
                          type="text"
                          value={cepValue}
                          onChange={(e) => {
                            const onlyNumbersRegex = /^[0-9]*$/;
                            const value = e.target.value;
                            if (onlyNumbersRegex.test(value)) {
                              setCepValue(e.target.value);
                            }
                          }}
                          maxLength={8}
                          minLength={8}
                          className="form-control"
                          required
                          placeholder="99999999"
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label text-black">
                          Endereço
                        </label>
                        <input
                          type="text"
                          value={addressValue}
                          onChange={(e) => setAddressValue(e.target.value)}
                          className="form-control"
                          required
                          minLength={5}
                          placeholder="Rua XXX XXXXXXX, 99"
                        />
                      </div>

                      <div className="mb-3">
                        <div className="row">
                          <div className="col-6">
                            <label className="form-label text-black">
                              Cidade
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={cityValue}
                              onChange={(e) => setCityValue(e.target.value)}
                              required
                              minLength={3}
                              placeholder="XXXXXX"
                            />
                          </div>
                          <div className="col-6">
                            <label className="form-label text-black mg-l-5">
                              Estado
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={stateValue}
                              minLength={2}
                              maxLength={15}
                              onChange={(e) => setStateValue(e.target.value)}
                              required
                              placeholder="XX"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="d-grid my-4 d-flex flex-column align-items-center">
                        <button
                          className="btn btn-primary"
                          style={{
                            borderRadius: 10,
                            backgroundColor: projectPrimaryColor,
                          }}
                          type={isLoading ? "button" : "submit"}
                        >
                          {isLoading ? (
                            <Spinner animation="border" size="sm"></Spinner>
                          ) : (
                            "Próximo"
                          )}
                        </button>

                        {errorMsg && (
                          <p className="mg-t-10 text-danger text-center">
                            {errorMsg}
                          </p>
                        )}
                      </div>
                    </form>

                    <p className="text-2 text-center text-black mb-0">
                      <span className="mx-1">
                        <Link
                          to={"/billing"}
                          onClick={(e) => {
                            e.preventDefault();
                            const newUrl = window.location.pathname;
                            history.push(newUrl);

                            setStep(eBillingStep.PLAN);
                          }}
                        >
                          Voltar
                        </Link>
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
